class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.onClick = this.handleClick.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this.onClick);
    document.addEventListener('submit', this.onSubmit);
    document.addEventListener('keydown', this.onKeydown);
    this.rotateCheckoutDeals();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('keydown', this.onKeydown);
  }

  handleClick(event) {
    const openButton = event.target.closest('[data-cart-open]');
    const closeButton = event.target.closest('[data-cart-close]');
    const quantityButton = event.target.closest('[data-cart-quantity]');
    const addButton = event.target.closest('[data-cart-add]');

    if (openButton) {
      event.preventDefault();
      this.open();
    } else if (closeButton) {
      this.close();
    } else if (quantityButton) {
      this.changeLine(quantityButton.dataset.lineKey, Number(quantityButton.dataset.cartQuantity));
    } else if (addButton) {
      this.addVariant(addButton.dataset.cartAdd, addButton);
    }
  }

  handleSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    this.addForm(form);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    this.rotateCheckoutDeals();
    this.loadRecommendations();
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.querySelector('.cart-drawer__panel')?.focus();
    });
  }

  async loadRecommendations() {
    const placeholder = this.querySelector('[data-cart-recommendations][data-section-url]');
    if (!placeholder || placeholder.dataset.loading === 'true') return;

    placeholder.dataset.loading = 'true';
    placeholder.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(placeholder.dataset.sectionUrl, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error('Aanbevelingen konden niet worden geladen.');

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const recommendations = parsed.querySelector('[data-cart-recommendations-content]');

      if (recommendations) placeholder.replaceWith(recommendations);
      else placeholder.remove();
    } catch (_error) {
      placeholder.dataset.loading = 'false';
      placeholder.removeAttribute('aria-busy');
    }
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    window.setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 300);
    this.previouslyFocused?.focus();
  }

  async addForm(form) {
    const button = form.querySelector('[type="submit"]');
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error('Dit product kon niet worden toegevoegd.');
      await this.refresh();
      this.open();
      this.announce('Product toegevoegd aan je winkelwagen.');
      document.dispatchEvent(new CustomEvent('product:added', { detail: { form, source: form.dataset.atcSource || 'main' } }));
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('pdp_add_to_cart_source', { source: form.dataset.atcSource || 'main' });
      }
    } catch (error) {
      this.announce(error.message);
      document.dispatchEvent(new CustomEvent('product:add-error', { detail: { form, message: error.message } }));
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  async addVariant(id, button) {
    button.disabled = true;
    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('Dit product kon niet worden toegevoegd.');
      await this.refresh();
      this.open();
      this.announce('Product toegevoegd aan je winkelwagen.');
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('pdp_add_to_cart_source', { source: button.dataset.atcSource || 'cross-sell' });
      }
    } catch (error) {
      this.announce(error.message);
    } finally {
      if (button.isConnected) button.disabled = false;
    }
  }

  async changeLine(key, quantity) {
    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('De winkelwagen kon niet worden bijgewerkt.');
      await this.refresh();
      this.open();
      this.announce('Winkelwagen bijgewerkt.');
    } catch (error) {
      this.announce(error.message);
    }
  }

  async refresh() {
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${this.root}?section_id=cart-drawer`),
      fetch(`${this.root}cart.js`),
    ]);
    const html = await sectionResponse.text();
    const cart = await cartResponse.json();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector('cart-drawer');
    if (nextDrawer) {
      this.innerHTML = nextDrawer.innerHTML;
      this.rotateCheckoutDeals();
    }
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
    });
  }

  announce(message) {
    const liveRegion = this.querySelector('[data-cart-live]');
    if (liveRegion) liveRegion.textContent = message;
  }

  rotateCheckoutDeals() {
    const groups = [...this.querySelectorAll('[data-checkout-deal-group]')];
    if (!groups.length) return;

    let seed;
    try {
      seed = Number(sessionStorage.getItem('dds-checkout-deal-seed'));
      if (!Number.isInteger(seed) || seed <= 0) {
        seed = Math.floor(Math.random() * 2147483646) + 1;
        sessionStorage.setItem('dds-checkout-deal-seed', String(seed));
      }
    } catch (_error) {
      seed = Math.floor(Math.random() * 2147483646) + 1;
    }

    groups.forEach((group, groupIndex) => {
      const cards = [...group.querySelectorAll('.cart-checkout-deal')];
      cards.forEach((card) => card.classList.remove('is-selected'));
      if (cards.length) cards[(seed + groupIndex * 17) % cards.length].classList.add('is-selected');
      group.classList.add('is-rotated');
    });
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
